import { PickupsResponseContentModel } from "app/data/pickup/models";
import { PlanningFreightItem, PlanningFreightProbill } from "app/data/linehaul-outbound-planning/models";
import { normalizeAppointmentOnFreight } from "./normalizeAppointmentOnFreight";

/** Flattened row from GET /pickups/inbound (freightTypes filter) */
type FlatInboundPickupRow = {
  pickupNumber?: string;
  probillNumber?: number;
  shipperName?: string;
  shipperAccountNumber?: number;
  priorityCustomer?: boolean;
  appointmentRequired?: boolean;
  hasAppointment?: boolean;
  appointmentStatus?: "SET" | "NEEDED" | "NONE" | string;
  appointmentDate?: string | null;
  appointmentEarlyTime?: string | null;
  appointmentLateTime?: string | null;
  appointmentConfirmedDateTime?: string | null;
  appointment?: {
    date?: string | null;
    earlyTime?: string | null;
    lateTime?: string | null;
    confirmedDateTime?: string | null;
    notes?: string | null;
  } | null;
  shipperAddress?: string;
  shipperCity?: string;
  shipperState?: string;
  shipperZip?: string;
  consigneeName?: string;
  consigneeCity?: string;
  consigneeState?: string;
  consigneeAddress1?: string;
  consigneePostalCode?: string;
  payorName?: string;
  payorAddress?: string;
  payorCity?: string;
  payorState?: string;
  payorZip?: string;
  bolNumber?: string;
  poNumber?: string;
  driverId?: number;
  driverName?: string;
  firstName?: string;
  lastName?: string;
  weight?: number;
  items?: number;
  eta?: string;
  manifestNumber?: number | string;
  status?: string;
  originServiceCenter?: {
    terminalNumber?: number;
    terminalName?: string;
    airportCode?: string;
  };
  destinationServiceCenter?: {
    terminalNumber?: number;
    terminalName?: string;
    airportCode?: string;
  };
  itemsList?: PickupsResponseContentModel["itemsList"];
};

type PickupListInput = PickupsResponseContentModel | FlatInboundPickupRow;

function isNestedPickup(row: PickupListInput): row is PickupsResponseContentModel {
  return row != null && typeof row === "object" && "pickup" in row && row.pickup != null;
}

function parseDimension(value: string | number | undefined): number | undefined {
  if (value == null || value === "") return undefined;
  const n = typeof value === "number" ? value : Number(String(value).replace(/[^\d.-]/g, ""));
  return Number.isFinite(n) ? n : undefined;
}

function splitDriverName(name: string | undefined): {
  driverFirstName?: string;
  driverLastName?: string;
} {
  if (!name?.trim()) return {};
  const parts = name.trim().split(/\s+/);
  return {
    driverFirstName: parts[0],
    driverLastName: parts.length > 1 ? parts.slice(1).join(" ") : undefined,
  };
}

function mapPickupItemsToPlanningItems(
  itemsList: PickupsResponseContentModel["itemsList"] | undefined,
  status: string
): PlanningFreightItem[] {
  return (itemsList ?? []).map((item, index) => ({
    itemSequenceNumber: index + 1,
    itemStatus: status,
    itemDescription:
      item.description?.trim() ||
      item.commodityCodeDescription?.trim() ||
      (item.commodityCode ? `Commodity Code: ${item.commodityCode}` : undefined) ||
      "—",
    pieces: item.units,
    weight: item.weight,
    length: parseDimension(item.length),
    width: parseDimension(item.width),
    height: parseDimension(item.height),
    itemType: item.packageType,
    sideMark: item.sidemark,
    itemCommodityCode: item.commodityCode,
    itemCommodityCodeDescription: item.commodityCodeDescription,
    style: item.ratingClass ? String(item.ratingClass) : undefined,
  }));
}

function mapNestedPickupToPlanningFreight(
  pickup: PickupsResponseContentModel
): PlanningFreightProbill {
  const req = pickup.pickupFullData?.pickupRequest;
  const nested = pickup.pickup;
  const driverName = nested?.driverName ?? "";
  const { driverFirstName, driverLastName } = splitDriverName(driverName);

  const squareYards = (pickup.itemsList ?? []).reduce(
    (sum, item) => sum + (Number(item.yards) || 0),
    0
  );

  const status = String(pickup.status ?? nested?.pickupStatus ?? "");

  return {
    probillNumber: req?.probillNumber ?? 0,
    pickupNumber: nested?.pickupNumber,
    pickupId: nested?.pickupId,
    driverId: nested?.driverId ?? req?.driverId,
    driverName: driverName || undefined,
    driverFirstName,
    driverLastName,
    bolNumber: req?.bolNumber || undefined,
    poNumber: req?.poNumber || undefined,
    etaDate: nested?.date || undefined,
    probillWeight: pickup.items?.weight,
    probillPieces: pickup.items?.count,
    probillSquareYards: squareYards > 0 ? squareYards : undefined,
    shipperName: req?.shipperName ?? pickup.shipper?.name,
    shipperNumber: req?.shipperAccountNumber,
    priorityCustomer: Boolean(
      (req as { priorityCustomer?: boolean } | undefined)?.priorityCustomer ??
        (pickup as { priorityCustomer?: boolean }).priorityCustomer
    ),
    appointmentRequired: Boolean(
      (req as { appointmentRequired?: boolean } | undefined)?.appointmentRequired ??
        (pickup as { appointmentRequired?: boolean }).appointmentRequired
    ),
    hasAppointment:
      (req as { hasAppointment?: boolean } | undefined)?.hasAppointment ??
      (pickup as { hasAppointment?: boolean }).hasAppointment,
    appointmentStatus:
      (req as { appointmentStatus?: string } | undefined)?.appointmentStatus ??
      (pickup as { appointmentStatus?: string }).appointmentStatus,
    appointmentDate:
      (req as { appointmentDate?: string | null } | undefined)?.appointmentDate ??
      (pickup as { appointmentDate?: string | null }).appointmentDate,
    appointmentEarlyTime:
      (req as { appointmentEarlyTime?: string | null } | undefined)?.appointmentEarlyTime ??
      (pickup as { appointmentEarlyTime?: string | null }).appointmentEarlyTime,
    appointmentLateTime:
      (req as { appointmentLateTime?: string | null } | undefined)?.appointmentLateTime ??
      (pickup as { appointmentLateTime?: string | null }).appointmentLateTime,
    appointmentConfirmedDateTime:
      (req as { appointmentConfirmedDateTime?: string | null } | undefined)
        ?.appointmentConfirmedDateTime ??
      (pickup as { appointmentConfirmedDateTime?: string | null }).appointmentConfirmedDateTime,
    appointment:
      (req as { appointment?: PlanningFreightProbill["appointment"] } | undefined)?.appointment ??
      (pickup as { appointment?: PlanningFreightProbill["appointment"] }).appointment ??
      null,
    shipperStreetAddress: req?.shipperAddress,
    shipperCity: req?.shipperCity,
    shipperState: req?.shipperState,
    shipperZipcode: req?.shipperZip,
    consigneeName: req?.consigneeName ?? pickup.consignee?.name,
    consigneeStreetAddress: req?.consigneeAddress1,
    consigneeCity: req?.consigneeCity,
    consigneeState: req?.consigneeState,
    consigneeZipcode: req?.consigneePostalCode,
    payorName: req?.payorName,
    payorStreetAddress: req?.payorAddress,
    payorCity: req?.payorCity,
    payorState: req?.payorState,
    payorZipcode: req?.payorZip,
    originTerminalNumber: pickup.originServiceCenter?.terminalNumber,
    originTerminalName: pickup.originServiceCenter?.terminalName,
    originTerminalAirportCode: pickup.originServiceCenter?.airportCode,
    destinationTerminalNumber: pickup.destinationServiceCenter?.terminalNumber,
    destinationTerminalName: pickup.destinationServiceCenter?.terminalName,
    destinationTerminalAirportCode: pickup.destinationServiceCenter?.airportCode,
    freightStatus: status,
    inboundManifestNumber:
      req?.manifestNumber != null ? String(req.manifestNumber) : undefined,
    items: mapPickupItemsToPlanningItems(pickup.itemsList, status),
  };
}

function mapFlatPickupToPlanningFreight(row: FlatInboundPickupRow): PlanningFreightProbill {
  const driverName = row.driverName ?? "";
  const nameFromParts =
    row.firstName || row.lastName
      ? {
          driverFirstName: row.firstName,
          driverLastName: row.lastName,
        }
      : splitDriverName(driverName);

  const status = String(row.status ?? "");
  const squareYards = (row.itemsList ?? []).reduce(
    (sum, item) => sum + (Number(item.yards) || 0),
    0
  );

  return {
    probillNumber: row.probillNumber ?? 0,
    pickupNumber: row.pickupNumber,
    driverId: row.driverId,
    driverName:
      driverName.trim() ||
      [nameFromParts.driverFirstName, nameFromParts.driverLastName].filter(Boolean).join(" ") ||
      undefined,
    driverFirstName: nameFromParts.driverFirstName,
    driverLastName: nameFromParts.driverLastName,
    bolNumber: row.bolNumber || undefined,
    poNumber: row.poNumber || undefined,
    etaDate: row.eta || undefined,
    probillWeight: row.weight,
    probillPieces: typeof row.items === "number" ? row.items : undefined,
    probillSquareYards: squareYards > 0 ? squareYards : undefined,
    shipperName: row.shipperName,
    shipperNumber: row.shipperAccountNumber,
    priorityCustomer: Boolean(row.priorityCustomer),
    appointmentRequired: Boolean(row.appointmentRequired),
    hasAppointment: row.hasAppointment,
    appointmentStatus: row.appointmentStatus,
    appointmentDate: row.appointmentDate,
    appointmentEarlyTime: row.appointmentEarlyTime,
    appointmentLateTime: row.appointmentLateTime,
    appointmentConfirmedDateTime: row.appointmentConfirmedDateTime,
    appointment: row.appointment ?? null,
    shipperStreetAddress: row.shipperAddress,
    shipperCity: row.shipperCity,
    shipperState: row.shipperState,
    shipperZipcode: row.shipperZip,
    consigneeName: row.consigneeName,
    consigneeStreetAddress: row.consigneeAddress1,
    consigneeCity: row.consigneeCity,
    consigneeState: row.consigneeState,
    consigneeZipcode: row.consigneePostalCode,
    payorName: row.payorName,
    payorStreetAddress: row.payorAddress,
    payorCity: row.payorCity,
    payorState: row.payorState,
    payorZipcode: row.payorZip,
    originTerminalNumber: row.originServiceCenter?.terminalNumber,
    originTerminalName: row.originServiceCenter?.terminalName,
    originTerminalAirportCode: row.originServiceCenter?.airportCode,
    destinationTerminalNumber: row.destinationServiceCenter?.terminalNumber,
    destinationTerminalName: row.destinationServiceCenter?.terminalName,
    destinationTerminalAirportCode: row.destinationServiceCenter?.airportCode,
    freightStatus: status,
    inboundManifestNumber:
      row.manifestNumber != null ? String(row.manifestNumber) : undefined,
    items: mapPickupItemsToPlanningItems(row.itemsList, status),
  };
}

export function mapPickupToPlanningFreight(pickup: PickupListInput): PlanningFreightProbill {
  if (pickup == null || typeof pickup !== "object") {
    return { probillNumber: 0, items: [] };
  }
  const mapped = isNestedPickup(pickup)
    ? mapNestedPickupToPlanningFreight(pickup)
    : mapFlatPickupToPlanningFreight(pickup as FlatInboundPickupRow);
  return normalizeAppointmentOnFreight(mapped);
}

export function mapPickupsToPlanningFreight(
  pickups: PickupListInput[]
): PlanningFreightProbill[] {
  return (pickups ?? [])
    .filter((row) => row != null && typeof row === "object")
    .map(mapPickupToPlanningFreight);
}
